import { useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useParams } from 'react-router';
import {
  LayoutDashboard, ReceiptText, UtensilsCrossed, CalendarClock, Users, BarChart3, Settings as SettingsIcon,
  Store,
  type LucideIcon,
} from 'lucide-react';
import { Shell, type NavGroup } from './components/Shell';
import { storefrontUrl } from './lib/links';
import { Stub, SettingsIndexExtra } from './components/Stub';
import { api } from './lib/api';
import { session } from './lib/auth';
import { ChooseScope } from './pages/ChooseScope';
import { ForgotPassword } from './pages/ForgotPassword';
import { Login } from './pages/Login';
import { SetPassword } from './pages/SetPassword';
import { OrderDetail } from './pages/OrderDetail';
import { OrdersFeed } from './pages/OrdersFeed';
import { Overview } from './pages/Overview';
import { MenuList } from './pages/menu/MenuList';
import { ItemEditor } from './pages/menu/ItemEditor';
import { PizzaSettings } from './pages/menu/PizzaSettings';
import { SoldOut } from './pages/menu/SoldOut';
import { Modifiers } from './pages/menu/Modifiers';
import { DaySheet } from './pages/bookings/DaySheet';
import { BookingDetail } from './pages/bookings/BookingDetail';
import { BookingSettings } from './pages/bookings/BookingSettings';
import { Directory } from './pages/customers/Directory';
import { Import } from './pages/customers/Import';
import { Coupons } from './pages/customers/Coupons';
import { Loyalty } from './pages/customers/Loyalty';
import { Reviews } from './pages/customers/Reviews';
import { ReportIndex } from './pages/reports/ReportIndex';
import { ReportView } from './pages/reports/ReportView';
import { Stores } from './pages/restaurant/Stores';
import { BrandProfile } from './pages/restaurant/BrandProfile';
import { Members } from './pages/restaurant/Members';
import { BrandReports } from './pages/restaurant/BrandReports';
import { Restaurants } from './pages/platform/Restaurants';
import { LiveOrders } from './pages/platform/LiveOrders';
import { PlatformReports } from './pages/platform/PlatformReports';
import { Printers } from './pages/platform/Printers';
import { FeedRuns } from './pages/platform/FeedRuns';
import { PlatformSettings } from './pages/platform/PlatformSettings';
import {
  Profile as SetProfile, Hours as SetHours, Ordering as SetOrdering,
  Payments as SetPayments, Channels as SetChannels, Devices as SetDevices,
  Tables as SetTables, Team as SetTeam, Pos as SetPos,
} from './pages/settings';
import { UberEatsCallback } from './pages/settings/Channels';
import { ScopeProvider, useScope } from './scope';
import { PLATFORM_PAGES, RESTAURANT_PAGES, STORE_AREAS, type Page } from './sitemap';

/** Signed-out visitors land on /login; everything else is behind this. */
function RequireAuth() {
  if (!session()) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function StoreShellInner() {
  const { rid, sid, restaurant, store } = useScope();
  const base = `/r/${rid}/s/${sid}`;
  const icons: Record<string, LucideIcon> = {
    overview: LayoutDashboard, orders: ReceiptText, menu: UtensilsCrossed,
    bookings: CalendarClock, customers: Users, reports: BarChart3, settings: SettingsIcon,
  };
  const groups: NavGroup[] = [
    {
      entries: STORE_AREAS.map((a) => ({
        label: a.label,
        to: a.nav ? `${base}/${a.nav}` : base,
        end: a.nav === '',
        icon: icons[a.key],
      })),
    },
    {
      // v3 parity: the storefront is one click away, because nobody should
      // have to assemble a customer URL by hand to look at their own store.
      entries: [
        {
          label: 'View storefront',
          to: storefrontUrl(rid, sid),
          external: true,
          icon: Store,
        },
      ],
    },
  ];
  return (
    <Shell
      groups={groups}
      scope={{
        restaurant: restaurant?.name ?? '…',
        store: store?.name ?? '…',
        to: '/choose',
      }}
      foot="Restoplus Admin · v4"
    />
  );
}

function StoreShell() {
  return (
    <ScopeProvider>
      <StoreShellInner />
    </ScopeProvider>
  );
}

function RestaurantShell() {
  const { rid } = useParams();
  const base = `/r/${rid}`;
  const [name, setName] = useState('…');
  useEffect(() => {
    let live = true;
    api.get<{ name: string }>(`/restaurants/${rid}`).then(
      (r) => { if (live) setName(r.name); },
      () => { if (live) setName('Restaurant'); },
    );
    return () => { live = false; };
  }, [rid]);
  const groups: NavGroup[] = [
    {
      label: 'Restaurant',
      entries: RESTAURANT_PAGES.map((p) => ({
        label: p.title,
        to: p.path ? `${base}/${p.path}` : base,
        end: p.path === '',
      })),
    },
  ];
  return (
    <Shell
      groups={groups}
      scope={{ restaurant: name, to: '/choose' }}
      foot="Restoplus Admin · v4"
    />
  );
}

function PlatformShell() {
  const groups: NavGroup[] = [
    {
      label: 'Platform',
      entries: PLATFORM_PAGES.map((p) => ({
        label: p.title,
        to: `/platform/${p.path}`,
      })),
    },
  ];
  return <Shell groups={groups} foot="Super-admin · Restoplus v4" />;
}

function StoreSettingsIndex(props: { page: Page }) {
  const { rid, sid } = useParams();
  const settings = STORE_AREAS.find((a) => a.key === 'settings')!;
  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">Store · Settings</div>
          <h1>{props.page.title}</h1>
        </div>
      </div>
      <SettingsIndexExtra pages={settings.pages} base={`/r/${rid}/s/${sid}`} />
    </>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route element={<RequireAuth />}>
      <Route path="/" element={<Navigate to="/choose" replace />} />
      <Route path="/choose" element={<ChooseScope />} />
      {/* Uber's OAuth redirect lands here; the signed state names the store */}
      <Route path="/integrations/uber-eats/callback" element={<UberEatsCallback />} />

      <Route path="/platform" element={<PlatformShell />}>
        <Route index element={<Navigate to="restaurants" replace />} />
        <Route path="restaurants" element={<Restaurants />} />
        <Route path="orders" element={<LiveOrders />} />
        <Route path="reports" element={<PlatformReports />} />
        <Route path="printers" element={<Printers />} />
        <Route path="google-food" element={<FeedRuns />} />
        <Route path="settings" element={<PlatformSettings />} />
      </Route>

      <Route path="/r/:rid" element={<RestaurantShell />}>
        <Route index element={<Stores />} />
        <Route path="settings" element={<BrandProfile />} />
        <Route path="members" element={<Members />} />
        <Route path="reports" element={<BrandReports />} />
      </Route>

      <Route path="/r/:rid/s/:sid" element={<StoreShell />}>
        {STORE_AREAS.flatMap((a) =>
          a.pages.map((p) => {
            const scope = `Store · ${a.label}`;
            if (p.path === '') return <Route key="index" index element={<Overview />} />;
            if (p.path === 'orders') return <Route key={p.path} path={p.path} element={<OrdersFeed />} />;
            if (p.path === 'menu') return <Route key={p.path} path={p.path} element={<MenuList />} />;
            if (p.path === 'menu/items/:itemId')
              return <Route key={p.path} path={p.path} element={<ItemEditor />} />;
            if (p.path === 'menu/pizza-settings')
              return <Route key={p.path} path={p.path} element={<PizzaSettings />} />;
            if (p.path === 'menu/modifiers') return <Route key={p.path} path={p.path} element={<Modifiers />} />;
            if (p.path === 'menu/sold-out')
              return <Route key={p.path} path={p.path} element={<SoldOut />} />;
            if (p.path === 'bookings') return <Route key={p.path} path={p.path} element={<DaySheet />} />;
            if (p.path === 'bookings/settings')
              return <Route key={p.path} path={p.path} element={<BookingSettings />} />;
            if (p.path === 'bookings/:bookingId')
              return <Route key={p.path} path={p.path} element={<BookingDetail />} />;
            if (p.path === 'customers') return <Route key={p.path} path={p.path} element={<Directory />} />;
            if (p.path === 'customers/import')
              return <Route key={p.path} path={p.path} element={<Import />} />;
            if (p.path === 'customers/coupons')
              return <Route key={p.path} path={p.path} element={<Coupons />} />;
            if (p.path === 'customers/loyalty')
              return <Route key={p.path} path={p.path} element={<Loyalty />} />;
            if (p.path === 'customers/reviews')
              return <Route key={p.path} path={p.path} element={<Reviews />} />;
            if (p.path === 'reports') return <Route key={p.path} path={p.path} element={<ReportIndex />} />;
            if (p.path === 'reports/:report')
              return <Route key={p.path} path={p.path} element={<ReportView />} />;
            if (p.path === 'orders/:orderId')
              return <Route key={p.path} path={p.path} element={<OrderDetail />} />;
            if (p.path === 'settings')
              return <Route key={p.path} path={p.path} element={<StoreSettingsIndex page={p} />} />;
            if (p.path === 'settings/profile')
              return <Route key={p.path} path={p.path} element={<SetProfile />} />;
            if (p.path === 'settings/hours')
              return <Route key={p.path} path={p.path} element={<SetHours />} />;
            if (p.path === 'settings/ordering')
              return <Route key={p.path} path={p.path} element={<SetOrdering />} />;
            if (p.path === 'settings/payments')
              return <Route key={p.path} path={p.path} element={<SetPayments />} />;
            if (p.path === 'settings/channels')
              return <Route key={p.path} path={p.path} element={<SetChannels />} />;
            if (p.path === 'settings/devices')
              return <Route key={p.path} path={p.path} element={<SetDevices />} />;
            if (p.path === 'settings/tables')
              return <Route key={p.path} path={p.path} element={<SetTables />} />;
            if (p.path === 'settings/team')
              return <Route key={p.path} path={p.path} element={<SetTeam />} />;
            if (p.path === 'settings/pos')
              return <Route key={p.path} path={p.path} element={<SetPos />} />;
            return <Route key={p.path} path={p.path} element={<Stub page={p} scope={scope} />} />;
          }),
        )}
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
